import { SearchOutlined } from '@ant-design/icons';
import { List, Spin } from 'antd';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { USER_API } from '../../api/user';
import Text from '../../components/Text';
import FadedText from '../../components/Text/FadedText';
import TextInput from '../../components/TextInput';
import { INTERNAL_LINKS } from '../../enum';
import useAuthUser from '../../hooks/useAuthUser';
import useDebounce from '../../hooks/useDebounce';
import { checkIfElementIsScrolledToBottom, getUserFullName } from '../../utils/common';
import { getMinutesAsMilliseconds } from '../../utils/numbers';
import { hasCompanyAdminRole, hasCompanyManagerRole, hasSystemAdminRole } from '../../utils/roles';
import { selectStoreCurrentCompany } from '../../utils/storeSelectors';
import classNames from './styles.module.scss';

const itemsPerPage = 25;

const GlobalSearch = props => {
  const { t, history } = props;
  const { authUser } = useAuthUser();
  const currentCompany = useSelector(selectStoreCurrentCompany);

  const [inputValue, setInputValue] = useState('');
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const debouncedValue = useDebounce(inputValue, 500);
  const searchInputRef = useRef(null);
  const resultsBoxRef = useRef(null);

  const [isSystemAdmin, isCompanyAdmin, isCompanyManager] = useMemo(() => {
    return [
      hasSystemAdminRole(authUser),
      hasCompanyAdminRole(authUser),
      hasCompanyManagerRole(authUser),
    ];
  }, [authUser]);

  const canSearchUsers = useMemo(() => isSystemAdmin || isCompanyAdmin || isCompanyManager, [
    isSystemAdmin,
    isCompanyAdmin,
    isCompanyManager,
  ]);

  const {
    data: userSearchData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['globalUserSearch', debouncedValue, isSystemAdmin, currentCompany?._id],
    queryFn: ({ pageParam = 1 }) => {
      const filters = {
        searchTerm: debouncedValue,
        globalSearch: isSystemAdmin,
      };

      if (isCompanyAdmin || isCompanyManager) {
        return USER_API.getUserList(
          currentCompany._id,
          filters,
          undefined,
          pageParam,
          itemsPerPage,
        );
      } else {
        return USER_API.getUserList(undefined, filters, undefined, pageParam, itemsPerPage);
      }
    },
    enabled: !!debouncedValue && canSearchUsers,
    getNextPageParam: (lastPage, pages) => {
      const { totalCount = 0 } = lastPage || {};
      const totalPages = Math.ceil(totalCount / itemsPerPage);
      if (pages.length < totalPages) return pages.length + 1;
    },
    staleTime: getMinutesAsMilliseconds(1),
  });

  const allUsers = userSearchData?.pages?.flatMap(page => page.users || []) || [];

  const filteredUsers = useMemo(() => {
    if (isCompanyAdmin || isCompanyManager) return allUsers;
    return allUsers.filter(user => !!get(user, 'company'));
  }, [isCompanyAdmin, isCompanyManager, allUsers]);

  const onSearch = useCallback(searchText => {
    setInputValue(searchText);
    setIsResultsVisible(!!searchText);
  }, []);

  const onSelect = useCallback(
    userId => {
      history.push(INTERNAL_LINKS.USER_DETAILS_PROFILE(userId));
      setInputValue('');
      setIsResultsVisible(false);
    },
    [history],
  );

  const handleInputFocus = useCallback(() => {
    if (inputValue) {
      setIsResultsVisible(true);
    }
  }, [inputValue]);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      setIsResultsVisible(false);
    }, 200);
  }, []);

  const handleResultsScroll = useCallback(
    e => {
      const bottom = checkIfElementIsScrolledToBottom(e.target);
      if (bottom && !isFetchingNextPage && hasNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const handleClickOutside = useCallback(event => {
    if (
      searchInputRef.current?.input &&
      !searchInputRef.current.input.contains(event.target) &&
      resultsBoxRef.current &&
      !resultsBoxRef.current.contains(event.target)
    ) {
      setIsResultsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (debouncedValue) {
      setIsResultsVisible(true);
    } else {
      setIsResultsVisible(false);
    }
  }, [debouncedValue]);

  if (!canSearchUsers) {
    return null;
  }

  return (
    <div className={classNames.globalSearchContainer}>
      <TextInput
        ref={searchInputRef}
        style={{ width: '100%' }}
        type="text"
        size="large"
        placeholder={t('searchByNameOrEmail')}
        className={classNames.searchInput}
        prefix={<SearchOutlined />}
        value={inputValue}
        onChange={e => onSearch(e.target.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        autoComplete="new-password"
      />

      {isResultsVisible && (
        <div ref={resultsBoxRef} className={classNames.resultsBox}>
          {isLoading ? (
            <div className={classNames.loadingContainer}>
              <Spin size="default" />
              <div className={classNames.loadingText}>
                <FadedText>{t('searching...')}</FadedText>
              </div>
            </div>
          ) : isError ? (
            <div className={classNames.errorContainer}>
              <FadedText>{t('error')}</FadedText>
            </div>
          ) : filteredUsers.length === 0 && debouncedValue ? (
            <div className={classNames.noResultsContainer}>
              <FadedText>{t('noResultsFoundForQuery', { searchString: debouncedValue })}</FadedText>
            </div>
          ) : filteredUsers.length > 0 ? (
            <List
              className={classNames.resultsList}
              dataSource={filteredUsers}
              onScroll={handleResultsScroll}
              renderItem={user => (
                <List.Item className={classNames.resultItem} onClick={() => onSelect(user._id)}>
                  <List.Item.Meta
                    title={
                      <div className={classNames.userInfo}>
                        <div className={classNames.userName}>
                          <Text variant="b">{getUserFullName(user)}</Text>
                        </div>
                        <div className={classNames.userEmail}>
                          <FadedText size="sm">{user.email}</FadedText>
                        </div>
                        <div className={classNames.userCompany}>
                          <FadedText variant="b" size="sm">
                            {get(user, 'company.name') || ''}
                          </FadedText>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
              footer={
                isFetchingNextPage ? (
                  <div className={classNames.footerContainer}>
                    <Spin size="small" />
                    <div className={classNames.footerText}>
                      <FadedText size="sm">{t('loading...')}</FadedText>
                    </div>
                  </div>
                ) : hasNextPage ? (
                  <div className={classNames.footerContainer}>
                    <FadedText size="sm">{t('scrollToLoadMore')}</FadedText>
                  </div>
                ) : null
              }
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
